import React, { useState, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { generateRandomAvatar, createAvatarSvg } from '../avatar/avatarUtils';
import { submitRoomAvatars, getGuessAvatarRoom } from './guessAvatarService';
import { getCurrentUserId } from '../../services/authService';
import MarkableAvatarCard from './MarkableAvatarCard';
import MarkSelectorModal from './MarkSelectorModal';
import './GuessAvatarRoomPage.css';

// Helper functions for localStorage management
const getGameState = (roomId) => {
  const stored = localStorage.getItem(`gameState_${roomId}`);
  if (!stored) return null;
  try {
    return JSON.parse(stored);
  } catch (e) {
    return null;
  }
};

const saveGameState = (roomId, roundNumber, avatarIndex) => {
  const state = { roundNumber, avatarIndex };
  localStorage.setItem(`gameState_${roomId}`, JSON.stringify(state));
};

const clearAvatarSelection = (roomId) => {
  const stored = getGameState(roomId);
  if (stored) {
    const newState = { roundNumber: stored.roundNumber, avatarIndex: null };
    localStorage.setItem(`gameState_${roomId}`, JSON.stringify(newState));
  }
};

function GuessAvatarRoomPage() {
  const { roomId } = useParams();
  const location = useLocation();
  const { initialData, mode: initialMode } = location.state || {};

  const [avatars, setAvatars] = useState([]);
  const [mode, setMode] = useState(initialMode || 'edit');
  const [marks, setMarks] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [showCopyToast, setShowCopyToast] = useState(false);
  const [showRefreshToast, setShowRefreshToast] = useState(false);
  const [selectedAvatarIndex, setSelectedAvatarIndex] = useState(null);
  const [roomOwner, setRoomOwner] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [myAvatarIndex, setMyAvatarIndex] = useState(null);
  const [showMyAvatar, setShowMyAvatar] = useState(true);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmingAvatarIndex, setConfirmingAvatarIndex] = useState(null);
  const [currentRoundNumber, setCurrentRoundNumber] = useState(null);
  const [showNewRoundToast, setShowNewRoundToast] = useState(false);

  // Check if current user is room owner
  const isRoomOwner = roomOwner && getCurrentUserId() === roomOwner;

  useEffect(() => {
    if (initialData && initialData.avatars) {
      const avatarsWithSvg = initialData.avatars.map(opts => ({
        svg: createAvatarSvg(opts),
        options: opts
      }));
      setAvatars(avatarsWithSvg);
      setMode('result');

      // Save room owner from API response
      if (initialData.room_owner) {
        setRoomOwner(initialData.room_owner);
      }

      // Check round_number change
      const newRoundNumber = initialData.round_number;
      const savedState = getGameState(roomId);

      if (savedState && savedState.roundNumber !== newRoundNumber) {
        // New round detected, clear avatar selection and show toast
        clearAvatarSelection(roomId);
        setMyAvatarIndex(null);
        setShowNewRoundToast(true);
        setTimeout(() => setShowNewRoundToast(false), 3000);
      } else if (savedState && savedState.avatarIndex !== null) {
        // Same round, restore avatar selection
        setMyAvatarIndex(savedState.avatarIndex);
      }

      setCurrentRoundNumber(newRoundNumber);
    } else {
      const initialAvatars = Array.from({ length: 20 }, () => generateRandomAvatar());
      setAvatars(initialAvatars);
      setMode('edit');

      // For creator, set room owner to current user
      const currentUserId = getCurrentUserId();
      setRoomOwner(currentUserId);
    }
    setIsLoading(false);
  }, [initialData, roomId]);

  const handleRegenerateOne = (index) => {
    const newAvatar = generateRandomAvatar();
    setAvatars(prev => {
      const updated = [...prev];
      updated[index] = newAvatar;
      return updated;
    });
  };

  const handleRegenerateAll = () => {
    const newAvatars = Array.from({ length: 20 }, () => generateRandomAvatar());
    setAvatars(newAvatars);
  };

  const handleSubmit = async () => {
    setError(null);
    setIsSubmitting(true);

    try {
      const payload = {
        roomId: parseInt(roomId, 10),
        avatars: avatars.map(a => a.options)
      };

      const response = await submitRoomAvatars(roomId, payload);
      const newRoundNumber = response?.round_number;

      // Save new round number and clear avatar selection for new round
      if (newRoundNumber !== undefined) {
        setCurrentRoundNumber(newRoundNumber);
        clearAvatarSelection(roomId);
        setMyAvatarIndex(null);

        // Show new round toast
        setShowNewRoundToast(true);
        setTimeout(() => setShowNewRoundToast(false), 3000);
      }

      setMode('result');
    } catch (err) {
      console.error('Failed to submit avatars:', err);
      setError(err.message || '提交失败，请稍后重试');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleMarkAvatar = (index) => {
    if (myAvatarIndex === null) {
      setConfirmingAvatarIndex(index);
      setShowConfirmDialog(true);
    } else {
      setSelectedAvatarIndex(index);
    }
  };

  const handleConfirmMyAvatar = () => {
    if (confirmingAvatarIndex !== null && currentRoundNumber !== null) {
      setMyAvatarIndex(confirmingAvatarIndex);
      saveGameState(roomId, currentRoundNumber, confirmingAvatarIndex);
      setShowConfirmDialog(false);
      setConfirmingAvatarIndex(null);
    }
  };

  const handleCancelConfirm = () => {
    setShowConfirmDialog(false);
    setConfirmingAvatarIndex(null);
  };

  const handleToggleMyAvatarDisplay = () => {
    setShowMyAvatar(prev => !prev);
  };

  const handleMarkSelect = (markType) => {
    if (selectedAvatarIndex !== null) {
      setMarks(prev => {
        const newMarks = { ...prev };
        if (prev[selectedAvatarIndex] === markType) {
          delete newMarks[selectedAvatarIndex];
        } else if (markType === null) {
          delete newMarks[selectedAvatarIndex];
        } else {
          newMarks[selectedAvatarIndex] = markType;
        }
        return newMarks;
      });
    }
    setSelectedAvatarIndex(null);
  };

  const handleCopyRoomId = async () => {
    try {
      await navigator.clipboard.writeText(roomId);
      setShowCopyToast(true);
      setTimeout(() => setShowCopyToast(false), 2000);
    } catch (err) {
      console.error('Failed to copy room ID:', err);
      setError('复制失败，请手动复制房间号');
    }
  };

  const handleBackToEdit = () => {
    setMode('edit');
    setMarks({});
  };

  const handleRefreshRoom = async () => {
    setError(null);
    setIsRefreshing(true);

    try {
      const roomData = await getGuessAvatarRoom(roomId);

      if (roomData && roomData.avatars) {
        const avatarsWithSvg = roomData.avatars.map(opts => ({
          svg: createAvatarSvg(opts),
          options: opts
        }));
        setAvatars(avatarsWithSvg);
        setMarks({});

        // Update room owner from API response
        if (roomData.room_owner) {
          setRoomOwner(roomData.room_owner);
        }

        // Check round_number change
        const newRoundNumber = roomData.round_number;
        const savedState = getGameState(roomId);

        if (savedState && savedState.roundNumber !== newRoundNumber) {
          // New round detected, clear avatar selection and show new round toast
          clearAvatarSelection(roomId);
          setMyAvatarIndex(null);
          setCurrentRoundNumber(newRoundNumber);
          setShowNewRoundToast(true);
          setTimeout(() => setShowNewRoundToast(false), 3000);
        } else {
          // Same round, just show refresh toast
          setShowRefreshToast(true);
          setTimeout(() => setShowRefreshToast(false), 2000);
        }

        if (newRoundNumber !== undefined) {
          setCurrentRoundNumber(newRoundNumber);
        }
      } else {
        setError('房间数据为空，无法刷新');
      }
    } catch (err) {
      console.error('Failed to refresh room:', err);
      setError(err.message || '刷新失败，请稍后重试');
    } finally {
      setIsRefreshing(false);
    }
  };

  if (isLoading) {
    return (
      <div className="room-loading">
        <div className="loading-spinner"></div>
        <p>加载中...</p>
      </div>
    );
  }

  return (
    <div className="guess-avatar-room-container">
      <header className="room-header">
        <div className="room-info">
          <h2 className="room-id-label">房间号:</h2>
          <div className="room-id-display">
            <span className="room-id-number">{roomId}</span>
            <button className="btn-copy" onClick={handleCopyRoomId} title="复制房间号">
              📋
            </button>
          </div>
          <span className="room-mode-badge">{mode === 'edit' ? '编辑模式' : '结果模式'}</span>

          {mode === 'result' && myAvatarIndex !== null && showMyAvatar && (
            <div className="my-avatar-small" onClick={handleToggleMyAvatarDisplay} title="点击隐藏我的头像">
              <div className="my-avatar-label">我的头像</div>
              <div
                className="my-avatar-display"
                dangerouslySetInnerHTML={{ __html: avatars[myAvatarIndex]?.svg }}
              />
            </div>
          )}

          {mode === 'result' && myAvatarIndex !== null && !showMyAvatar && (
            <button className="btn-show-my-avatar" onClick={handleToggleMyAvatarDisplay} title="点击显示我的头像">
              👁️ 显示我的头像
            </button>
          )}
        </div>

        {mode === 'edit' && (
          <div className="room-actions">
            <button
              className="btn-regenerate-all"
              onClick={handleRegenerateAll}
              disabled={isSubmitting}
            >
              🎲 全部刷新
            </button>
            <button
              className="btn-submit"
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? '提交中...' : '✓ 确定提交'}
            </button>
          </div>
        )}

        {mode === 'result' && (
          <div className="room-actions">
            <p className="room-hint">
              {myAvatarIndex === null ? '请先选择一个头像作为你的头像' : '点击头像进行标记'}
            </p>
            <div className="result-mode-buttons">
              <button
                className="btn-refresh-room"
                onClick={handleRefreshRoom}
                disabled={isRefreshing}
              >
                {isRefreshing ? '刷新中...' : '🔄 刷新房间数据'}
              </button>
              {isRoomOwner && (
                <button className="btn-back-to-edit" onClick={handleBackToEdit}>
                  ← 返回编辑
                </button>
              )}
            </div>
          </div>
        )}
      </header>

      {error && (
        <div className="error-banner">
          <span className="error-icon">⚠️</span>
          <span className="error-message">{error}</span>
          <button className="error-close" onClick={() => setError(null)}>
            ×
          </button>
        </div>
      )}

      <div className="avatar-grid">
        {avatars.map((avatar, index) => (
          <MarkableAvatarCard
            key={index}
            avatarData={avatar}
            index={index}
            mark={marks[index] || null}
            onMark={handleMarkAvatar}
            isEditable={mode === 'edit'}
            onRegenerate={handleRegenerateOne}
          />
        ))}
      </div>

      {showCopyToast && (
        <div className="copy-toast">
          ✓ 房间号已复制!
        </div>
      )}

      {showRefreshToast && (
        <div className="refresh-toast">
          ✓ 房间数据已刷新!
        </div>
      )}

      {showNewRoundToast && (
        <div className="new-round-toast">
          🎮 新一轮游戏开始，请重新选择你的头像!
        </div>
      )}

      {showConfirmDialog && confirmingAvatarIndex !== null && (
        <>
          <div className="confirm-dialog-overlay" onClick={handleCancelConfirm} />
          <div className="confirm-dialog">
            <h3 className="confirm-dialog-title">选择我的头像</h3>
            <div className="confirm-dialog-preview">
              <div
                className="confirm-avatar-display"
                dangerouslySetInnerHTML={{ __html: avatars[confirmingAvatarIndex]?.svg }}
              />
            </div>
            <p className="confirm-dialog-text">确定选择这个头像作为你的头像吗？</p>
            <p className="confirm-dialog-hint">选择后将无法更改，这个头像会用于其他人猜测。</p>
            <div className="confirm-dialog-actions">
              <button className="btn-cancel" onClick={handleCancelConfirm}>
                取消
              </button>
              <button className="btn-confirm" onClick={handleConfirmMyAvatar}>
                确定选择
              </button>
            </div>
          </div>
        </>
      )}

      <MarkSelectorModal
        isOpen={selectedAvatarIndex !== null}
        onClose={() => setSelectedAvatarIndex(null)}
        onSelect={handleMarkSelect}
        currentMark={selectedAvatarIndex !== null ? marks[selectedAvatarIndex] : null}
      />
    </div>
  );
}

export default GuessAvatarRoomPage;
